import api from '@/lib/axios';

import type {
  NotificationDto,
  NotificationListPayload,
  NotificationListResponse,
  UnreadCountResponse,
} from '@/types/notification';

// ============================================
// Query Params
// ============================================

interface GetNotificationsParams {
  page?: number;
  limit?: number;
  status?: string;
  type?: string;
}

// ============================================
// Notifications REST API
// ============================================

/** GET /v1/notifications — paginated list */
export const getNotifications = async (
  params: GetNotificationsParams = {},
): Promise<NotificationListResponse> => {
  const response = await api.get<NotificationListPayload>(
    '/notification-service/v1/notifications',
    { params },
  );
  // Backend list endpoint emits `{ items, meta }`; map `items` → `data`.
  return { data: response.data.items, meta: response.data.meta };
};

/** GET /v1/notifications/{id} — single notification */
export const getNotificationById = async (id: string): Promise<NotificationDto> => {
  const response = await api.get<NotificationDto>(
    `/notification-service/v1/notifications/${id}`,
  );
  return response.data;
};

/** GET /v1/notifications/unread-count — returns count number */
export const getUnreadCount = async (): Promise<number> => {
  const response = await api.get<UnreadCountResponse>(
    '/notification-service/v1/notifications/unread-count',
  );
  return response.data.count;
};

/** PATCH /v1/notifications/{id}/read — mark single as read (204) */
export const markAsRead = async (id: string): Promise<void> => {
  await api.patch(`/notification-service/v1/notifications/${id}/read`);
};

/** PATCH /v1/notifications/read-all — mark all as read (204) */
export const markAllAsRead = async (): Promise<void> => {
  await api.patch('/notification-service/v1/notifications/read-all');
};

/** DELETE /v1/notifications/{id} — archive notification (204) */
export const archiveNotification = async (id: string): Promise<void> => {
  await api.delete(`/notification-service/v1/notifications/${id}`);
};
